// src/data/announcements.jsx
//
// The running "What's New" tally — every note the fleet has been shown, newest
// first. Two surfaces read this list:
//   - the More-menu red dot / unread count in TopNav
//   - WhatsNew page (/whats-new): the full history, with From-the-Developer
//     notes highlighted
// No note ever pops a modal (Jill 2026-08-14): new notes light the dot and
// wait in the queue. The old AnnouncementModal is gone; don't bring a popup
// back without asking her.
//
// Two sources feed the list:
//   - rows Jill posts from Admin > Post to the Fleet (announcements table),
//     mirrored into a localStorage cache by refreshAnnouncements()
//   - the bundled ANNOUNCEMENTS array below, now EMPTY: every note lives in
//     the table so Jill can edit or unpublish it without a deploy
//
// HARD RULE (project-offline-pwa): every READER here is bundled data +
// localStorage only — no network calls, so the badge/page can never hang
// or lock anyone out on dead boat wifi. The ONE network touch is
// refreshAnnouncements(), a fail-silent 5s-deadlined background refresh that
// AuthenticatedLayout fires after login; when it can't reach the server the
// cache just stays stale.
//
// Entry shape:
//   id        — stable, never reused; seen-tracking is keyed on it, so keep
//               historical ids exactly as first shipped
//   date      — "YYYY-MM-DD", the day the note went live
//   type      — "feature" (release note) | "developer" (a personal note from
//               Jill — highlighted on the page)
//   audience(roles, boardPosition, committeePositions) — return true to show
//   title/body — body is JSX; keep paragraphs short, phones read this
import React from "react";
import { supabase } from "../supabaseClient";
import { withTimeout } from "../lib/submitQueue";
import {
    mergeAnnouncements, isVisibleTo, paragraphs, readCache, writeCache,
    SEEN_KEY, SEEN_EVENT,
} from "../lib/announcementsCore";

export const ANNOUNCEMENTS = [
    // EMPTY ON PURPOSE — and it should stay that way.
    //
    // Every What's New note now lives in the announcements TABLE, so Jill can
    // edit, unpublish, or delete any of them from Admin > Post to the Fleet
    // without a deploy. The last three code-shipped notes moved into the table
    // on 2026-09-13 (migration 20260913190000) under their exact original ids,
    // which is what kept everyone's read state intact.
    //
    // To ship a new note: INSERT a row (stable text-slug id) or just post it
    // from Post to the Fleet. Adding an entry back here would make a note Jill
    // cannot touch without calling for a code change — the exact problem this
    // removal fixed.
    //
    // The merge machinery below still honours bundled entries, so this array
    // is a working escape hatch if a note ever has to ship as code again.
];

// Seen-tracking: same key format the old single-note modal used, so existing
// dismissals carry over untouched. SEEN_KEY/SEEN_EVENT live in the core module
// (plain JS, node-testable) and are re-exported here for the components.
export { SEEN_KEY, SEEN_EVENT };

// The full list: bundled entries + Jill's posted rows from the local cache.
// Synchronous and network-free by design.
export function allAnnouncements() {
    return mergeAnnouncements(ANNOUNCEMENTS, readCache());
}

export function visibleAnnouncements(ctx = {}) {
    return allAnnouncements().filter((a) => isVisibleTo(a, ctx));
}

// Fail-silent background refresh of the posted-announcements cache. Fired
// from AuthenticatedLayout after login; 5s deadline so dead boat wifi (which
// hangs, not fails) can never stall anything. On success the SEEN_EVENT nudges
// the badge/modal/page to re-read the merged list.
let refreshInFlight = false;
export async function refreshAnnouncements() {
    if (refreshInFlight) return;
    refreshInFlight = true;
    try {
        const { data, error } = await withTimeout(
            supabase
                .from("announcements")
                .select("id, posted_on, type, title, body, audience_roles, active")
                .eq("active", true),
            5000
        );
        if (!error && Array.isArray(data)) {
            writeCache(data);
            window.dispatchEvent(new Event(SEEN_EVENT));
        }
    } catch { /* offline / dead wifi — the cache just stays stale */ }
    finally { refreshInFlight = false; }
}

// Body renderer for both sources: bundled entries carry JSX, posted rows carry
// plain text that renders as blank-line-separated paragraphs.
export function renderBody(entry) {
    if (typeof entry.body !== "string") return entry.body;
    const ps = paragraphs(entry.body);
    return ps.map((p, i) => (
        <p key={i} style={{ marginTop: i === 0 ? 0 : undefined, marginBottom: i === ps.length - 1 ? 0 : undefined }}>
            {p}
        </p>
    ));
}

export function isSeen(uid, id) {
    // Private mode (localStorage throws): treat everything as seen — never nag
    // a browser that can't remember the dismissal.
    try { return !!localStorage.getItem(SEEN_KEY(id, uid)); }
    catch { return true; }
}

export function unseenCount(uid, ctx) {
    if (!uid) return 0;
    return visibleAnnouncements(ctx).filter((a) => !isSeen(uid, a.id)).length;
}

export function markSeen(uid, ids) {
    if (!uid) return;
    const list = Array.isArray(ids) ? ids : [ids];
    try {
        const stamp = new Date().toISOString();
        list.forEach((id) => localStorage.setItem(SEEN_KEY(id, uid), stamp));
    } catch { /* private mode — nothing to record */ }
    window.dispatchEvent(new Event(SEEN_EVENT));
}
